<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { clone } from '@/util/utils';
import EzButton from '@/components/ui/Button';
import EzRadioButton from '@/components/ui/RadioButton/EzRadioButton.vue';
import EzOnOff from '@/components/ui/OnOff';
import EzForm from '@/components/ui/Form';
import flash from '@/components/ui/FlashMessage';
import EzInput from '@/components/ui/Input/EzInput.vue';
import EzImageUpload from '@/components/ui/ImageUpload/EzImageUpload.vue';
import EzTextarea from '@/components/ui/Textarea/EzTextarea.vue';
import { LOADING_KEY } from '@/util/constants';

/**
 * B2BSettings
 * @version 1.0.0
 * @since 3.18.0
 */

export default {
  name: 'B2BSettings',
  components: {
    EzButton,
    EzRadioButton,
    EzOnOff,
    EzForm,
    EzInput,
    EzImageUpload,
    EzTextarea,
  },
  data() {
    return {
      formKey: 'b2b-settings',
      loadingKeyForm: LOADING_KEY.FETCH_DISTRIBUTOR_B2B,
      values: {
        active: false,
      },
      initialValues: {},
      imgModified: false,
      loginImgModified: false,
      hasLogo: false,
    };
  },
  computed: {
    ...mapState('entities/users', ['loggedUser']),
    requiredFieldsPopulated() {
      if (!this.values.active) return true;
      return !!this.values?.domain && this.hasLogo;
    },
    saveDisabled() {
      return !this.requiredFieldsPopulated;
    },
    canEditProduct() {
      return this.$permission.has('editProductInfo');
    },
  },
  methods: {
    ...mapActions('entities/distributors', ['fetchDistributorB2BSettings']),
    ...mapMutations('entities/users', ['SET_LOGGED_USER']),
    updateState(key, val) {
      this.values[key] = val;
      this.values = { ...this.values };
    },
    async onSubmitSuccess() {
      flash.success({ title: `${this.$t('global.distributor')} settings successfully updated!` });
    },
    onRemoveImage() {
      this.values.logo = null;
      this.imgModified = true;
      this.hasLogo = false;
    },
    addLogo() {
      this.hasLogo = true;
    },
    onRemoveLoginImage() {
      this.values.loginImage = null;
      this.loginImgModified = true;
    },
    transformer(data) {
      if (!this.values.active) data = { active: this.values.active.toString() };
      else {
        if (!this.values.showPreviouslyOrdered) data.append('showPreviouslyOrdered', 'false');
        if (!this.values.approvalRequired) data.append('approvalRequired', 'false');
        if (!this.values.public) data.append('public', 'false');
      }
      return data;
    },
    openLoginPreview() {
      window.open(`https://${this.values.domain}.orderez.co/login`);
    },
    copyToClipboard() {
      navigator.clipboard.writeText(`https://${this.values.domain}.orderez.co` || '');
      flash.success({
        title: 'Domain copied!',
        message: 'The domain has been copied successfully.',
      });
    },
  },
  async created() {
    const { data } = await this.fetchDistributorB2BSettings();
    this.values = clone(data.data);
    this.initialValues = clone(data.data);
    this.hasLogo = !!this.values.logo;
  },
};
</script>

<template>
  <div class="product-settings container--small">
    <ez-form
      :formKey="formKey"
      :loading-key="loadingKeyForm"
      :transformer="transformer"
      :submitType="values.active ? 'multipart' : 'json'"
      action="/distributor/b2b-portal"
      method="put"
      @success="onSubmitSuccess"
      :additional-data="{
        ...(imgModified && { logo: '' }),
        ...(loginImgModified && { loginImage: '' }),
      }"
      ref="form"
    >
      <div class="product-settings__section product-settings__section--toggle">
        <div class="help-block">
          <h4 class="help-block__title custom-title">Branded B2B Portal</h4>
          <p class="help-block__text mb-0">
            Enable to use a branded portal for your customers to log into and place orders.
          </p>
        </div>
        <ez-on-off
          :formKey="formKey"
          name="active"
          :value="values.active.toString()"
          :checked="values.active"
          @change="updateState('active', $event)"
        />
      </div>
      <template v-if="values.active">
        <div
          class="product-settings__section product-settings__section--expiryDays"
          @click="copyToClipboard"
        >
          <div class="help-block mt-24">
            <h4 class="help-block__title custom-title">B2B Portal Domain</h4>
          </div>
          <ez-input
            class="mt-16"
            :formKey="formKey"
            placeholder="Your Domain"
            :readonly="!!initialValues.domain"
            disabledTooltip="Click to copy. Please contact OrderEZ support if you wish to change the domain."
            :tooltip="
              !!initialValues.domain
                ? 'Click to copy. Please contact OrderEZ support if you wish to change the domain.'
                : ''
            "
            label="Domain"
            type="text"
            name="domain"
            :value="values.domain || ''"
            @onChange="updateState('domain', $event)"
          >
            <template #suffix>.orderez.co</template>
          </ez-input>
        </div>
        <hr />
        <div class="product-settings__section product-settings__section--expiryDays">
          <div class="help-block mt-24">
            <h4 class="help-block__title custom-title">Login Page</h4>
            <p class="help-block__text mb-16">Customize your Login Page.</p>
          </div>
          <ez-image-upload
            :formKey="formKey"
            :previewUrl="values.logo"
            :isDefaultImage="!values.logo"
            @change="imgModified = true"
            :is-rect="true"
            name="logo"
            @removeImage="onRemoveImage()"
            @addImage="addLogo()"
            :remove-enabled="false"
          >
            <template #addImage>
              <span v-if="values.logo"> Change Logo</span>
              <span v-else>Add Logo</span>
            </template>
          </ez-image-upload>
          <ez-input
            class="mt-16"
            :formKey="formKey"
            placeholder="Login Page Header"
            label="Header"
            type="text"
            name="loginHeader"
            :value="values.loginHeader || ''"
          />
          <ez-textarea
            class="mt-16"
            name="loginText"
            label="Text"
            :formKey="formKey"
            :value="values?.loginText ?? null"
            placeholder="Add Login Page Text"
          />
          <label class="input-group__label mt-16">Image</label>
          <ez-image-upload
            :formKey="formKey"
            :isDefaultImage="!values.loginImage"
            :previewUrl="values.loginImage"
            @change="loginImgModified = true"
            name="loginImage"
            @removeImage="onRemoveLoginImage()"
          >
            <template #addImage>
              <span v-if="values.loginImage"> Change Image</span>
              <span v-else>Add Image</span>
            </template>
            <template #removeImage>
              <font-awesome-icon icon="times" />
              <span>Remove Image</span>
            </template>
          </ez-image-upload>
        </div>
        <ez-button @click="openLoginPreview" class="mt-16" type="secondary" isFullWidth
          >Preview Login Page</ez-button
        >
        <hr />
        <div class="product-settings__section">
          <div class="help-block">
            <h4 class="help-block__title custom-title">Default Customer View</h4>
            <p class="help-block__text mb-16">
              Select which type of view customer’s will see by default.
            </p>
          </div>
          <ez-radio-button
            :formKey="formKey"
            class="mb-16"
            value="grid"
            name="customerView"
            label="Grid"
            :data="values?.customerView || false"
            @change="updateState('customerView', $event)"
          />
          <ez-radio-button
            :formKey="formKey"
            class="mb-16"
            value="list"
            name="customerView"
            label="List"
            :data="values?.customerView || 'list'"
            @change="updateState('customerView', $event)"
          />
        </div>
        <hr />
        <div class="product-settings__section">
          <div class="help-block">
            <h4 class="help-block__title custom-title">Products sorting</h4>
            <p class="help-block__text mb-16">
              Select which sorting should be applied to the products list.
            </p>
          </div>
          <ez-radio-button
            :formKey="formKey"
            class="mb-16"
            value="most_ordered"
            name="productSort"
            label="Most Ordered"
            :data="values?.productSort || false"
            @change="updateState('productSort', $event)"
          />
          <ez-radio-button
            :formKey="formKey"
            class="mb-16"
            value="alphabetical"
            name="productSort"
            label="Alphabetical"
            :data="values?.productSort || 'alphabetical'"
            @change="updateState('productSort', $event)"
          />
        </div>
        <hr v-if="false" />
        <div v-if="false" class="product-settings__section product-settings__section--toggle">
          <div class="help-block">
            <h4 class="help-block__title custom-title">Previously Ordered</h4>
            <p class="help-block__text mb-0">
              When enabled, Customers will see a section displaying previously ordered products.
            </p>
          </div>
          <ez-on-off
            :formKey="formKey"
            name="showPreviouslyOrdered"
            :value="(values?.showPreviouslyOrdered || false).toString()"
            :checked="values?.showPreviouslyOrdered"
            @change="updateState('showPreviouslyOrdered', $event)"
          />
        </div>
        <hr />
        <div class="product-settings__section product-settings__section--toggle">
          <div class="help-block">
            <h4 class="help-block__title custom-title">Approval Required for B2B Portal Access</h4>
            <p class="help-block__text mb-0">
              Require users Registering, without an Onboarding Link, to wait for approval before
              having access to the portal.
            </p>
          </div>
          <ez-on-off
            :formKey="formKey"
            name="approvalRequired"
            :value="(values?.approvalRequired || false).toString()"
            :checked="values?.approvalRequired"
            @change="updateState('approvalRequired', $event)"
          />
        </div>
        <hr />
        <div class="product-settings__section product-settings__section--toggle">
          <div class="help-block">
            <h4 class="help-block__title custom-title">Public Access</h4>
            <p class="help-block__text mb-0">Allow public access to the B2B portal.</p>
          </div>
          <ez-on-off
            :formKey="formKey"
            name="public"
            :value="(values?.public || false).toString()"
            :checked="values?.public"
            @change="updateState('public', $event)"
          />
        </div>
      </template>
      <template #buttons>
        <ez-button
          class="mt-24"
          form-type="submit"
          :disabled="saveDisabled"
          disabledTooltip="Logo and Domain are required."
        >
          Save Changes
        </ez-button>
      </template>
    </ez-form>
  </div>
</template>

<style lang="scss" scoped>
.product-settings {
  &__section {
    &--toggle {
      display: flex;
      align-items: flex-start;

      .help-block {
        margin-right: 16px;
        flex-grow: 1;
      }
    }
    .input-group__label {
      display: block;
      margin-bottom: 0.375rem;
      color: #6c7995;
      font-size: 12px;
      line-height: 14px;
      font-weight: 500;
      text-transform: capitalize;
    }

    &--expiryDays,
    &--lowInventory {
      :deep() .mask-input-wrapper {
        max-width: 64px;
        margin-left: 8px;
        margin-right: 8px;

        .mask-input__input {
          font-weight: normal;
        }
      }

      .text-inline-input {
        width: 100%;
        display: flex;
        align-items: center;
      }
    }

    .help-block {
      margin-bottom: 0;

      &__title {
        &.custom-title {
          font-weight: 600;
        }
      }

      &__text {
        @include font-size(14px, 20px);
      }
    }

    :deep() .input-group {
      .ez-radio-button__label {
        color: $color-gray-25;
      }

      &__input {
        input:disabled::placeholder {
          color: $color-gray-6C;
        }
        input[name='domain'] {
          padding-right: 96px;
          &:read-only {
            cursor: not-allowed;
            color: $color-gray-6C;
          }
        }

        .input-group__suffix {
          right: 8px;
        }
      }
    }

    .mask-input :deep() input {
      font-weight: 400;
    }
  }
}
</style>
